// useMergedWorkflowTemplates.js

import { useEffect, useMemo, useState } from 'react';

import {
  mergeWorkflowTemplates,
  subscribeToBaseWorkflowTemplates,
} from 'services/workflows/BaseWorkflowTemplateService';
import { normalizeEligibleMissionTemplates } from 'node/missions/mission/utils/missionRunUtils';

/**
 * useMergedWorkflowTemplates
 *
 * Merges type-bag workflow templates with base-scoped `_workflowTemplates`
 * for the active base + type (system types rely on the base overlay).
 *
 * @param {object} params
 * @param {object|null} [params.typeDef]
 * @param {string|null} [params.baseKey]
 * @param {string|null} [params.typeKey]
 * @returns {{ templates: object[], baseTemplates: object[], isReady: boolean }}
 */
export default function useMergedWorkflowTemplates({
  typeDef = null,
  baseKey = null,
  typeKey = '',
} = {}) {
  const resolvedTypeKey = String(typeKey || typeDef?.key || typeDef?.type || '').trim();
  const resolvedBaseKey = String(baseKey || '').trim();

  const typeTemplates = useMemo(() => {
    return normalizeEligibleMissionTemplates(typeDef);
  }, [typeDef]);

  const [baseTemplates, setBaseTemplates] = useState([]);
  const [isReady, setIsReady] = useState(!resolvedBaseKey || !resolvedTypeKey);

  useEffect(() => {
    if (!resolvedBaseKey || !resolvedTypeKey) {
      setBaseTemplates([]);
      setIsReady(true);
      return undefined;
    }

    setIsReady(false);
    return subscribeToBaseWorkflowTemplates(
      { baseKey: resolvedBaseKey, nodeType: resolvedTypeKey },
      (rows) => {
        setBaseTemplates(Array.isArray(rows) ? rows : []);
        setIsReady(true);
      },
    );
  }, [resolvedBaseKey, resolvedTypeKey]);

  const templates = useMemo(() => {
    return mergeWorkflowTemplates(typeTemplates, baseTemplates);
  }, [baseTemplates, typeTemplates]);

  return {
    templates,
    baseTemplates,
    typeTemplates,
    isReady,
  };
}
